import { RuleType } from '../../rules/IRule.ts';
import { type SectionType } from '../../sections/SectionTypeContext.tsx';
import { type WidgetType } from '../Widget.ts';
import { hasWidgetCreationDefaults } from './hasWidgetCreationDefaults.ts';
import { type IInputWidget } from './inputs/IInputWidget.ts';
import { WidgetRepository } from './WidgetRepository.ts';

/**
 * Feature flag "obbligatorietà di default dei widget di input".
 *
 * L'attivazione è separata per tipo di builder:
 * `ENABLE_WIDGET_DEFAULT_REQUIRED_FRONTOFFICE` per le sezioni frontoffice e
 * `ENABLE_WIDGET_DEFAULT_REQUIRED_BACKOFFICE` per le sezioni dati backoffice.
 */
export class WidgetDefaultRequiredLogic {
  static isEnabled(sectionType: SectionType): boolean {
    if (sectionType === 'FRONTOFFICE') {
      return process.env.ENABLE_WIDGET_DEFAULT_REQUIRED_FRONTOFFICE === 'true';
    }
    if (sectionType === 'BACKOFFICE') {
      return process.env.ENABLE_WIDGET_DEFAULT_REQUIRED_BACKOFFICE === 'true';
    }
    return false;
  }

  /**
   * Partial da passare a `WidgetRepository.getInitial` alla creazione di un
   * nuovo widget nel builder: attiva la regola Required solo se il default è
   * attivo (`enabled`, dal Popover "Creazione nuovi Elementi"), la sezione è
   * FRONTOFFICE o BACKOFFICE e il tipo di widget supporta la regola. Negli
   * altri casi ritorna `undefined` e la creazione resta invariata.
   */
  static getInitialWidgetPartial(
    widgetType: WidgetType,
    sectionType: SectionType,
    // Obbligatorio: un default silenzioso scavalcherebbe la scelta del
    // configuratore nei punti di chiamata che dimenticano il parametro.
    enabled: boolean,
  ): Pick<IInputWidget, 'rules'> | undefined {
    if (!enabled) return undefined;
    if (!hasWidgetCreationDefaults(sectionType)) return undefined;

    const type = WidgetRepository.find(widgetType);
    const hasRequiredRule = type?.rules?.some(
      rule => rule.type === RuleType.Required,
    );
    if (!hasRequiredRule) return undefined;

    // `value: true` esplicito: `RulesSchema` scarta le regole vuote in fase
    // di cast, quindi `{ required: {} }` non sopravviverebbe a `getInitial`.
    return { rules: { [RuleType.Required]: { value: true } } };
  }
}
